import {
  LETTER_4_SCALE,
  NUMERIC_SCALE_PRESETS,
  passingGradeOf,
  readScaledLabel,
  resolveGradingScale,
  type GradingScale,
} from '@dosfilos/domain';

// ─── La escala de notas del seminario, en la pantalla ───────────────────────
// UNA forma de escribir y de leer una nota final en toda la web: con la escala
// del seminario (grading-scale.ts del dominio), nunca suponiendo 1-7.
//
// TOLERA LA FORMA VIEJA A PROPÓSITO. Hosting se despliega ANTES que el backend:
// durante esa ventana la pantalla nueva habla con callables que todavía
// devuelven la escala como texto ('1-7', '0-100', 'letter-4') o nada. Ese texto
// se traduce aquí a la escala que significaba.

export type ScaleLike = GradingScale | '1-7' | '0-100' | 'letter-4' | string | null | undefined;

/** La escala, venga como venga. Sin dato, la de siempre (1-7). */
export function toScale(raw: ScaleLike): GradingScale {
  if (raw && typeof raw === 'object') return raw;
  if (raw === 'letter-4') return LETTER_4_SCALE;
  if (raw === '0-100') return NUMERIC_SCALE_PRESETS['0-100'];
  if (raw === '0-10' || raw === '0-20') return NUMERIC_SCALE_PRESETS[raw];
  return resolveGradingScale(null);
}

/** Cómo se escribe una nota final: su etiqueta si la tiene (lo que dice el
 *  acta); si no, el número con los decimales de la escala. «—» sin nota. */
export function formatGrade(
  grade: { value?: number | null | undefined; label?: string | null | undefined },
  scale: GradingScale,
): string {
  const label = grade.label?.trim();
  if (label) return label;
  const v = grade.value;
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return scale.kind === 'numeric' ? v.toFixed(scale.decimals) : String(v);
}

/** Si una nota final aprueba según la escala. Null si no se puede decir. Con
 *  etiqueta decide la etiqueta; un número solo se juzga en escala numérica. */
export function gradePasses(
  grade: { value?: number | null | undefined; label?: string | null | undefined },
  scale: GradingScale,
  coursePassing?: number | null,
): boolean | null {
  const label = grade.label?.trim();
  if (label) return readScaledLabel(label, scale, coursePassing)?.passes ?? null;
  const v = grade.value;
  if (typeof v !== 'number' || scale.kind !== 'numeric') return null;
  return readScaledLabel(String(v), scale, coursePassing)?.passes ?? null;
}

/** El nombre corto de la escala, para leyendas («1–7», «letras A–F»). */
export function scaleRange(scale: GradingScale): string {
  if (scale.kind === 'numeric') return `${scale.min}–${scale.max}`;
  const labels = scale.rows.map((r) => r.label);
  return `${labels[0]}–${labels[labels.length - 1]}`;
}

/** El recorrido del PROMEDIO: en números, la escala; en letras, sus puntos
 *  («0–4» en letter-4). */
export function averageRange(scale: GradingScale): string {
  if (scale.kind === 'numeric') return `${scale.min}–${scale.max}`;
  const points = scale.rows.map((r) => r.points);
  return `${Math.min(...points)}–${Math.max(...points)}`;
}

/** La nota de aprobación, escrita en la escala: la del curso o la de la
 *  escala; en letras, la letra más baja que aprueba. */
export function passingText(scale: GradingScale, coursePassing?: number | null): string {
  if (scale.kind === 'numeric') {
    return passingGradeOf(scale, coursePassing).toFixed(scale.decimals);
  }
  const passing = scale.rows.filter((r) => r.passes);
  return passing[passing.length - 1]?.label ?? '—';
}

/** Las letras que se ofrecen al escribir una nota final. Si la escala ignora
 *  los signos (la de 4 puntos, como en LABTS), «B+» y «B-» son notas válidas
 *  —valen lo que la «B»— y tienen que poder elegirse: la tabla solo trae las
 *  letras base. Las que no aprueban (F) van sin signo. Una nota ya escrita que
 *  no esté en la lista se ofrece igual, para que nunca se vea vacía. */
export function letterOptions(scale: GradingScale, current?: string | null): string[] {
  if (scale.kind !== 'letter') return [];
  const out: string[] = [];
  for (const row of scale.rows) {
    const base = row.label.trim().toUpperCase();
    if (scale.signsIgnored && row.passes && /^[A-Z]$/.test(base)) {
      out.push(`${base}+`, base, `${base}-`);
    } else {
      out.push(base);
    }
  }
  const c = current?.trim().toUpperCase();
  if (c && !out.includes(c)) out.push(c);
  return out;
}
